import {AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import {DataFrameColumnRelationship} from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipSelector';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {JoinType} from 'pipelines/joins/JoinConstants';
import {AddStepParamsForStep, PreviewStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

/**
 * Form state for `CombineDataFramesView`
 * @package
 */
export interface CombineDataFramesState {
  combineMethod: PipelineStepType.JOIN | PipelineStepType.UNION_DATASETS;
  joinType: JoinType;
  leftTable: AnalysisViewGraphQL | null;
  rightTable: AnalysisViewGraphQL | null;
  relationships: Array<DataFrameColumnRelationship>;
  dataFrameName: string;
  dataFrameNameHasUserEdits: boolean;
}

/** @package */
export const DEFAULT_COMBINE_DATA_FRAMES_STATE: CombineDataFramesState = {
  combineMethod: PipelineStepType.JOIN,
  joinType: 'LEFT_OUTER',
  leftTable: null,
  rightTable: null,
  relationships: [],
  dataFrameName: '',
  dataFrameNameHasUserEdits: false,
};

/** @package */
export const combineDataFramesGlobalState = makeUseGlobalClientState<CombineDataFramesState>(
  DEFAULT_COMBINE_DATA_FRAMES_STATE
);

/** @package */
export function isRelationshipComplete(relationship: DataFrameColumnRelationship): boolean {
  return relationship.leftColumn != null && relationship.rightColumn != null;
}

function getParamsFromTable(
  table: AnalysisViewGraphQL | null
): {dataFrameId: string; stepInfo: {stepId: string; stepDataName: string}} | null {
  if (table == null) {
    return null;
  }

  const lineageNode = table.lineage[table.lineage.length - 1];
  return {
    dataFrameId:
      lineageNode.stepOutputReference!.outputStepData.rawDatas[lineageNode.stepOutputReference!.rawDataIndex!]
        .id,
    stepInfo: {
      stepId: lineageNode.step.id,
      stepDataName: lineageNode.stepOutputReference!.outputStepData.name,
    },
  };
}

function getJoinStepParams(
  joinType: JoinType,
  leftTable: AnalysisViewGraphQL | null,
  rightTable: AnalysisViewGraphQL | null,
  relationships: Array<DataFrameColumnRelationship>,
  dataFrameName: string
): {
  addStepParams: AddStepParamsForStep<PipelineStepType.JOIN, 0> | null;
  previewStepParams: PreviewStepParams<PipelineStepType.JOIN> | null;
} {
  let addStepConfig: PipelineStepConfig<PipelineStepType.JOIN, 0>;
  let previewStepConfig: PipelineStepConfig<PipelineStepType.JOIN, 0>;

  const leftParams = getParamsFromTable(leftTable);
  const rightParams = getParamsFromTable(rightTable);

  if (leftParams == null || rightParams == null || dataFrameName === '') {
    return {addStepParams: null, previewStepParams: null};
  }

  if (joinType === 'POSITIONAL') {
    const joinConfig = {
      joinType,
      joinConditions: [],
    };
    addStepConfig = {
      dataFrameName,
      ...joinConfig,
    };
    // Use empty string for preview to prevent re-running preview when name changes
    previewStepConfig = {
      dataFrameName: '',
      ...joinConfig,
    };
  } else {
    if (relationships.length === 0 || relationships.some((r) => !isRelationshipComplete(r))) {
      return {addStepParams: null, previewStepParams: null};
    }
    const joinConfig = {
      joinType,
      joinConditions: relationships.map(({leftColumn, rightColumn}) => ({
        leftDataSourceColumn: leftColumn!,
        rightDataSourceColumn: rightColumn!,
      })),
    };
    addStepConfig = {
      dataFrameName,
      ...joinConfig,
    };
    // Use empty string for preview to prevent re-running preview when name changes
    previewStepConfig = {
      dataFrameName: '',
      ...joinConfig,
    };
  }
  const addStepParams: AddStepParamsForStep<PipelineStepType.JOIN, 0> = {
    stepType: PipelineStepType.JOIN,
    stepVersion: 0,
    stepConfig: addStepConfig,
    stepInput: {
      leftDataFrameId: leftParams.dataFrameId,
      rightDataFrameId: rightParams.dataFrameId,
    },
    stepInputParents: {
      Left: [leftParams.stepInfo],
      Right: [rightParams.stepInfo],
    },
  };
  const previewStepParams: PreviewStepParams<PipelineStepType.JOIN, 0> = {
    stepType: PipelineStepType.JOIN,
    stepConfig: previewStepConfig,
    stepInput: {
      leftDataFrameId: leftParams.dataFrameId,
      rightDataFrameId: rightParams.dataFrameId,
    },
  };
  return {addStepParams, previewStepParams};
}

function getUnionStepParams(
  leftTable: AnalysisViewGraphQL | null,
  rightTable: AnalysisViewGraphQL | null,
  selectedUnionPrimaryColumns: Array<DataFrameColumnDef | null>,
  selectedUnionSecondaryColumns: Array<DataFrameColumnDef | null>,
  dataFrameName: string
): {
  addStepParams: AddStepParamsForStep<PipelineStepType.UNION_DATASETS, 0> | null;
  previewStepParams: PreviewStepParams<PipelineStepType.UNION_DATASETS> | null;
} {
  const leftParams = getParamsFromTable(leftTable);
  const rightParams = getParamsFromTable(rightTable);

  if (
    leftParams == null ||
    rightParams == null ||
    selectedUnionPrimaryColumns.length === 0 ||
    selectedUnionSecondaryColumns.length === 0 ||
    selectedUnionPrimaryColumns.some((c) => c == null) ||
    selectedUnionSecondaryColumns.some((c) => c == null) ||
    dataFrameName === ''
  ) {
    return {addStepParams: null, previewStepParams: null};
  }
  const stepConfig: PipelineStepConfig<PipelineStepType.UNION_DATASETS, 0> = {
    dataFrameName,
    primaryDataSourceColumns: selectedUnionPrimaryColumns.map((col) => col!.systemName),
    secondaryDataSourcesColumns: [selectedUnionSecondaryColumns.map((col) => col!.systemName)],
    includeDuplicates: true,
  };
  const addStepParams: AddStepParamsForStep<PipelineStepType.UNION_DATASETS, 0> = {
    stepType: PipelineStepType.UNION_DATASETS,
    stepVersion: 0,
    stepConfig,
    stepInput: {
      primaryDataFrameId: leftParams.dataFrameId,
      secondaryDataFrameId: rightParams.dataFrameId,
    },
    stepInputParents: {
      'Primary data frame': [leftParams.stepInfo],
      'Secondary data frames': [rightParams.stepInfo],
    },
  };
  const previewStepParams: PreviewStepParams<PipelineStepType.UNION_DATASETS, 0> = {
    stepType: PipelineStepType.UNION_DATASETS,
    // Use empty string for preview dataFrameName to prevent re-running preview when name changes
    stepConfig: {...stepConfig, dataFrameName: ''},
    stepInput: {
      'Primary data frame': leftParams.dataFrameId,
      'Secondary data frames': rightParams.dataFrameId,
    },
  };
  return {addStepParams, previewStepParams};
}

type StepParams =
  | {
      addStepParams: AddStepParamsForStep<PipelineStepType.JOIN, 0> | null;
      previewStepParams: PreviewStepParams<PipelineStepType.JOIN> | null;
    }
  | {
      addStepParams: AddStepParamsForStep<PipelineStepType.UNION_DATASETS, 0> | null;
      previewStepParams: PreviewStepParams<PipelineStepType.UNION_DATASETS> | null;
    };

/**
 * Convert form state to parameters for AnalysisActions.addOrReplaceAndRunAnalysisStep
 * Returns null when state is not ready, else return valid parameters
 * @package
 */
export function getStepParams(
  combineMethod: PipelineStepType.JOIN | PipelineStepType.UNION_DATASETS,
  joinType: JoinType,
  leftTable: AnalysisViewGraphQL | null,
  rightTable: AnalysisViewGraphQL | null,
  relationships: Array<DataFrameColumnRelationship>,
  selectedUnionPrimaryColumns: Array<DataFrameColumnDef | null>,
  selectedUnionSecondaryColumns: Array<DataFrameColumnDef | null>,
  dataFrameName: string
): StepParams {
  if (combineMethod === PipelineStepType.JOIN) {
    return getJoinStepParams(joinType, leftTable, rightTable, relationships, dataFrameName);
  } else if (combineMethod === PipelineStepType.UNION_DATASETS) {
    return getUnionStepParams(
      leftTable,
      rightTable,
      selectedUnionPrimaryColumns,
      selectedUnionSecondaryColumns,
      dataFrameName
    );
  }
  // oxlint-disable-next-line no-unused-expressions
  combineMethod satisfies never;
  return {
    addStepParams: null,
    previewStepParams: null,
  };
}
